import { CartComponentBase } from '@utilities/cart-component-base';
import { publish, EVENTS } from '@utilities/events';
import { decodeHTMLEntities, batchUpdateElements } from '@utilities/theme';

class CartItem extends CartComponentBase {
  SKIP_SAVINGS_RENDER = false;
  constructor() {
    super();

    /** @type {Object} Component configuration */
    this.config = null;

    /** @type {number} Current quantity */
    this.currentQuantity = 0;

    /** @type {number} Last known good quantity (for error recovery) */
    this.lastKnownGoodQuantity = 0;

    /** @type {number} Pending quantity change timeout */
    this.quantityChangeTimeout = null;

    /** @type {number} Debounce delay in milliseconds */
    this.debounceDelay = 500;

    /** @type {boolean} Whether a quantity change is currently pending */
    this.isQuantityChangePending = false;

    // Bind event handler to maintain context
    this.handleClick = this.handleClick.bind(this);
    this.handleInput = this.handleInput.bind(this);
    this.handleStateChange = this.handleStateChange.bind(this);
  }

  handleClick(event) {
    const target = event.target.closest("[data-action]");
    if (!target) return;

    event.preventDefault();

    const action = target.dataset.action;

    if (this.allowDebug()) {
      console.log("CartItem action:", {
        action,
        target: target.tagName,
      });
    }

    switch (action) {
      case "remove":
        this.#handleRemove();
        break;
      case "increase":
        this.#requestQuantityChange(1, 1);
        break;
      case "decrease":
        this.#requestQuantityChange(-1, 1);
        break;
      case "quantity-input":
        // Handle input focus, but actual changes are handled by input event
        break;
      default:
        if (this.allowDebug()) {
          console.warn("Unknown cart action:", action);
        }
    }
  }

  handleInput(event) {
    if (event.target.matches(".quantity__input")) {
      const newQuantity = parseInt(event.target.value, 10) || 0;
      const quantityDifference = newQuantity - this.currentQuantity;

      if (this.allowDebug()) {
        console.log("Direct quantity input change:", {
          oldQuantity: this.currentQuantity,
          newQuantity,
          difference: quantityDifference,
        });
      }

      // Only update if the quantity actually changed
      if (quantityDifference !== 0) {
        // Use the same pattern as button clicks for consistency
        this.#requestQuantityChange(quantityDifference, 1);
      }
    }
  }

  /**
   * Handle remove action
   *
   * MEMBERSHIP BUNDLE REMOVAL BEHAVIOR:
   * When ANY item in a membership bundle is removed, the ENTIRE bundle must be removed.
   * This is because membership bundles represent a cohesive package where items are
   * often discounted or included as part of the membership benefits.
   *
   * Items are identified as membership bundles by:
   * - Having a bundleId (from _gc:bundle-ID property)
   * - Having is_loyalty_bundle: true (from _gc:membership-bundle property)
   *
   * Example: Black Wolf Membership bundle contains:
   * - Main membership product ($1000)
   * - Wolf Headcover (discounted to Free via "Included with membership")
   * - Putter Cover (discounted to Free via "Included with membership")
   * - Snapback (discounted to Free via "Included with membership")
   *
   * If user removes any of these items, ALL items must be removed together.
   *
   * @private
   */
  #handleRemove() {
    if (this.allowDebug()) {
      console.log("Removing item:", {
        config: this.config,
        bundleId: this.config.bundleId,
        isBundlePiece: this.config.isBundlePiece,
        isLoyaltyBundle: this.config.isLoyaltyBundle,
      });
    }

        /*
    A Cart Transform function collapses bundle parts into a single line item
    server-side, so every cart line — bundle or not — is removed the same way.

    bundleId/isBundlePiece/isBundleMain/isLoyaltyBundle are retained on config
    (sourced from cart-json enrichment of line-item properties) for order-tracking
    purposes outside the theme, but no longer drive removal branching here.
    */
    this.#requestItemRemoval();
  }

  /**
   * Request individual item removal via direct API call
   * @private
   */
  async #requestItemRemoval() {
    if (this.allowDebug()) {
      console.log("CartItem: Requesting individual item removal", {
        key: this.config.key,
        title: this.config.title
      });
    }

    // Show loading state
    this.classList.add("cart-item--updating");

    try {
      // Get the current cart state manager (always live reference)
      const stateManager = this.#getCurrentStateManager();

      if (!stateManager) {
        throw new Error("CartStateManager not available");
      }

      if (this.allowDebug()) {
        console.log("CartItem: Removing item via the standard action");
      }

      // Route through the standard action so shopify:cart:lines-update fires.
      const { removeCartLinesViaStandardAction } = await import("@utilities/theme");
      await removeCartLinesViaStandardAction(this.config.key);

      if (this.allowDebug()) {
        console.log("CartItem: Item removed successfully");
      }

      // Membership/loyalty bundles drive reactive signup/checkout CTAs elsewhere
      // (membership-actions.js, welcome-package-modal.js) — this event has no
      // Shopify Standard equivalent and must keep firing on successful removal.
      if (this.config.bundleId && this.config.isLoyaltyBundle) {
        publish(EVENTS.CART_MEMBERSHIP_BUNDLE_REMOVED, {
          membership: this.config.handle,
        });
      }

      // Clear any existing errors on successful removal
      this.#clearError();
    } catch (error) {
      this._handleError(error, "removing cart item");

      // Show user-friendly error message
      this.#showError(
        error.message || "Unable to remove item. Please try again."
      );
    } finally {
      this.classList.remove("cart-item--updating");
    }
  }

  /**
   * Validate the requested quantity against inventory limits
   * @param {number} newQuantity - The requested quantity
   * @returns {boolean} True if quantity is valid, false otherwise
   * @private
   */
  #validateQuantityAgainstInventory(newQuantity) {
    // If no inventory data available, allow the request to proceed
    if (!this.config.inventoryQuantity || !this.config.inventoryPolicy) {
      return true;
    }

    // If inventory policy is not 'deny', allow the request
    if (this.config.inventoryPolicy !== "deny") {
      return true;
    }

    // Check if requested quantity exceeds available inventory
    if (newQuantity > this.config.inventoryQuantity) {
      const maxQuantity = this.config.inventoryQuantity;

      if (this.allowDebug()) {
        console.log("Quantity validation failed:", {
          requestedQuantity: newQuantity,
          availableInventory: maxQuantity,
          inventoryPolicy: this.config.inventoryPolicy,
        });
      }

      // Show error message
      this.#showError(
        `Only ${maxQuantity} ${
          maxQuantity === 1 ? "is" : "are"
        } available in stock.`
      );

      // Reset quantity display to last known good quantity
      this.lastKnownGoodQuantity = maxQuantity;
      this.currentQuantity = maxQuantity;
      this.#updateQuantityDisplay(maxQuantity);

      return false;
    }

    return true;
  }

  /**
   * Get the current cart state manager (always live reference)
   * @returns {CartStateManager} The current cart state manager instance
   * @private
   */
  #getCurrentStateManager() {
    return this.cartStateManager;
  }

  /**
   * Execute the actual quantity change API call
   * @param {number} newQuantity - The new quantity to set
   * @private
   */
  async #executeQuantityChange(newQuantity) {
    if (this.allowDebug()) {
      console.log("Executing quantity change:", {
        newQuantity,
        lastKnownGood: this.lastKnownGoodQuantity,
      });
    }

    // Validate quantity against inventory limits before making API call
    if (!this.#validateQuantityAgainstInventory(newQuantity)) {
      // Validation failed - error message already shown in validation method
      return;
    }

    try {
      // Get the current cart state manager (always live reference)
      const stateManager = this.#getCurrentStateManager();

      if (!stateManager) {
        throw new Error("CartStateManager not available");
      }

      if (this.allowDebug()) {
        console.log("CartItem: Using state manager for quantity change", {
          managerType: stateManager.constructor?.name,
          hasUpdateItem: typeof stateManager.updateItem === "function",
          isUpdating: stateManager.isUpdating,
          currentState: stateManager.getState
            ? {
                itemCount: stateManager.getState().itemCount,
                hasOurItem: !!stateManager
                  .getState()
                  .items?.find((item) => item.key === this.config.key),
              }
            : "no getState method",
        });
      }

      // A Cart Transform function collapses bundle parts into a single line item
      // server-side, so every cart line — bundle or not — is quantity-changed the
      // same way. bundleId/isBundlePiece are retained on config for order-tracking
      // purposes outside the theme but no longer drive branching here.
      //
      // A quantity of 0 removes the line, so one call covers both cases. Routing
      // through the standard action is what makes shopify:cart:lines-update fire.
      if (this.allowDebug()) {
        console.log("CartItem: Setting quantity via the standard action", {
          key: this.config.key,
          newQuantity,
        });
      }

      const { updateCartLineViaStandardAction } = await import("@utilities/theme");
      await updateCartLineViaStandardAction(this.config.key, newQuantity);

      if (this.allowDebug()) {
        console.log("Quantity updated via the standard action");
      }

      // Update last known good quantity on success
      this.lastKnownGoodQuantity = newQuantity;

      // Clear any existing errors on successful update
      this.#clearError();
    } catch (error) {
      this._handleError(error, "updating cart quantity");

      /**
       * 422 errors are returned when Shopify detects an "unprocessable entity."
       * This could be from missing data such as variantId or quantity.
       * This could be returned from a cart / checkout validation function.
       * This could be returned from inventory quantity validation.
       */
      const is422Error = error.status === 422;

      if (is422Error) {
        if (this.allowDebug()) {
          console.log(
            "422 error detected, reverting to last known good quantity:",
            this.lastKnownGoodQuantity
          );
        }

        // Revert to last known good quantity
        this.currentQuantity = this.lastKnownGoodQuantity;
        this.#updateQuantityDisplay(this.lastKnownGoodQuantity);
      }

      // Show user-friendly error message
      this.#showError(
        error.message || "Unable to update quantity. Please try again."
      );
    }
  }

  /**
   * Handle cart state changes
   * @param {Object} data - The cart state change data
   */
  handleStateChange(data) {
    const { state, changes } = data;

    if (this.allowDebug()) {
      console.log("CartItem received state change:", {
        state,
        changes,
        itemKey: this.config.key,
        source: data?.source || 'unknown',
        forced: data?.forced || false
      });
    }

    // Find this item in the new state
    const updatedItem = state.items.find(
      (item) => item.key === this.config.key
    );

    if (updatedItem) {
      // Clear any error messages when state updates successfully
      this.#clearError();

      // Update the current quantity and last known good quantity if it changed
      if (updatedItem.quantity !== this.currentQuantity) {
        this.currentQuantity = updatedItem.quantity;
        this.lastKnownGoodQuantity = updatedItem.quantity;
        this.#updateQuantityDisplay(updatedItem.quantity);
      }

      // Update item data with cart state data
      // (full UI data will be handled separately via state update event)
      this.#updateItemData(updatedItem);
    } else {
      // Item was removed from cart
      this.#handleItemRemoved();
    }
  }

  /**
   * Update item data based on cart state
   * @param {Object} updatedItem - The updated item data from cart state
   * @private
   */
  #updateItemData(updatedItem) {
    if (this.allowDebug()) {
      console.log("CartItem: Updating item data from cart state:", {
        key: updatedItem.key,
        hasPriceHtml: !!updatedItem.priceHtml,
        currentConfigPriceHtml: !!this.config.priceHtml,
      });
    }

    // Store previous config for change detection
    const previousConfig = { ...this.config };

    // Update the configuration with new item data
    this.config.quantity = updatedItem.quantity;
    this.config.lineLevelTotalDiscountMoney = updatedItem.lineLevelTotalDiscountMoney;
    this.config.lineLevelTotalDiscount = updatedItem.lineLevelTotalDiscount;
    this.config.finalLinePrice = updatedItem.finalLinePrice;
    this.config.finalLinePriceMoney = updatedItem.finalLinePriceMoney;

    // Update price_html if available in the cart state
    if (updatedItem.priceHtml) {
      this.config.priceHtml = updatedItem.priceHtml;
    }

    // Update any other item-specific data as needed
    // Note: Some properties like discount_allocations might not be in basic cart state
    if (updatedItem.discountAllocations) {
      this.config.discountAllocations = updatedItem.discountAllocations;
    }

    // Perform selective updates based on what actually changed
    this.#performSelectiveUpdates(previousConfig, this.config);
  }

  /**
   * Update cart item with fresh item data from state
   * This method is called when the cart state refreshes
   * @param {Object} itemData - Fresh item data from cart state
   */
  updateFromState(itemData) {
    if (this.allowDebug()) {
      console.log("CartItem: Updating with fresh item data:", {
        key: itemData.key,
        title: itemData.title,
        oldPriceHtml: this.config.priceHtml,
        newPriceHtml: itemData.priceHtml,
      });
    }

    // Store previous config for comparison
    const previousConfig = { ...this.config };

    // Update configuration with fresh item data
    this.config = {
      ...this.config,
      ...itemData,
      // Preserve current runtime state
      quantity: this.currentQuantity,
      finalLinePrice: this.config.finalLinePrice,
    };

    // Perform selective updates based on what actually changed
    this.#performSelectiveUpdates(previousConfig, this.config);
  }

  /**
   * Perform selective updates based on detected changes between previous and current config
   * @param {Object} previousConfig - Previous configuration
   * @param {Object} currentConfig - Current configuration
   * @private
   */
  #performSelectiveUpdates(previousConfig, currentConfig) {
    const changes = this.#detectChanges(previousConfig, currentConfig);

    if (this.allowDebug() && Object.keys(changes).length > 0) {
      console.log("CartItem: Detected changes requiring updates:", changes);
    }

    // Handle each type of change with specific update methods
    if (changes.quantity) {
      this.#updateQuantityElements(currentConfig.quantity);
    }

    if (changes.price) {
      this.#updatePriceElements(currentConfig);
    }

    if (changes.discounts) {
      this.#updateDiscountElements(currentConfig);
    }

    if (changes.messages) {
      this.#updateMessageElements(currentConfig);
    }

    if (changes.availability) {
      this.#updateAvailabilityElements(currentConfig);
    }

    if (changes.media) {
      this.#updateMediaElements(currentConfig);
    }

    if (changes.titles) {
      this.#updateTitleElements(currentConfig);
    }

    // If there are structural changes that can't be handled selectively, fall back to re-render
    if (changes.requiresRerender) {
      if (this.allowDebug()) {
        console.log(
          "CartItem: Structural changes detected, performing full re-render"
        );
      }
      this.#render();

      // Re-attach event listeners after re-render
      this.addEventListener("click", this.handleClick);
      this.addEventListener("input", this.handleInput);
    }
  }

  /**
   * Detect what changed between previous and current configuration
   * @param {Object} previousConfig - Previous configuration
   * @param {Object} currentConfig - Current configuration
   * @returns {Object} Object describing what changed
   * @private
   */
  #detectChanges(previousConfig, currentConfig) {
    const changes = {};

    // Quantity changes
    if (previousConfig.quantity !== currentConfig.quantity) {
      changes.quantity = {
        from: previousConfig.quantity,
        to: currentConfig.quantity,
      };
    }

    // Price-related changes
    const priceFields = [
      "priceHtml",
      "finalLinePrice",
      "finalLinePriceMoney",
      "originalLinePrice",
      "lineLevelTotalDiscount",
      "lineLevelTotalDiscountMoney",
    ];
    const priceChanged = priceFields.some(
      (field) => previousConfig[field] !== currentConfig[field]
    );
    if (priceChanged) {
      changes.price = {
        fields: priceFields.filter(
          (field) => previousConfig[field] !== currentConfig[field]
        ),
        newPriceHtml: currentConfig.priceHtml,
      };
    }

    // Discount changes
    const discountFields = [
      "lineLevelTotalDiscount",
      "lineLevelTotalDiscountMoney",
      "lineItemCombinedTotalDiscount",
      "lineItemCombinedTotalDiscountMoney",
      "discountAllocations",
    ];
    const discountChanged = discountFields.some((field) => {
      if (field === "discountAllocations") {
        return (
          JSON.stringify(previousConfig[field]) !==
          JSON.stringify(currentConfig[field])
        );
      }
      return previousConfig[field] !== currentConfig[field];
    });
    if (discountChanged) {
      changes.discounts = {
        fields: discountFields.filter((field) => {
          if (field === "discountAllocations") {
            return (
              JSON.stringify(previousConfig[field]) !==
              JSON.stringify(currentConfig[field])
            );
          }
          return previousConfig[field] !== currentConfig[field];
        }),
      };
    }

    // Message changes
    const messageFields = [
      "hasPreorderMessage",
      "preorderMessage",
      "hasMinicartMessage",
      "minicartMessage",
      "message",
    ];
    const messageChanged = messageFields.some(
      (field) => previousConfig[field] !== currentConfig[field]
    );
    if (messageChanged) {
      changes.messages = {
        fields: messageFields.filter(
          (field) => previousConfig[field] !== currentConfig[field]
        ),
      };
    }

    // Availability changes
    const availabilityFields = [
      "available",
      "inventoryQuantity",
      "inventoryPolicy",
    ];
    const availabilityChanged = availabilityFields.some(
      (field) => previousConfig[field] !== currentConfig[field]
    );
    if (availabilityChanged) {
      changes.availability = {
        fields: availabilityFields.filter(
          (field) => previousConfig[field] !== currentConfig[field]
        ),
      };
    }

    // Media changes (less common but possible)
    if (
      JSON.stringify(previousConfig.featuredMedia) !==
      JSON.stringify(currentConfig.featuredMedia)
    ) {
      changes.media = {
        from: previousConfig.featuredMedia,
        to: currentConfig.featured_media,
      };
    }

    // Title changes (rare but possible)
    const titleFields = ["title", "productTitle", "variantTitle"];
    const titleChanged = titleFields.some(
      (field) => previousConfig[field] !== currentConfig[field]
    );
    if (titleChanged) {
      changes.titles = {
        fields: titleFields.filter(
          (field) => previousConfig[field] !== currentConfig[field]
        ),
      };
    }

    // Check for structural changes that require full re-render
    const structuralFields = [
      "isFinalSale",
      "isGift",
      "isGiftCard",
      "disableQuantity",
      "isBundleMain",
      "isBundlePiece",
      "isLoyaltyBundle",
    ];
    const structuralChanged = structuralFields.some(
      (field) => previousConfig[field] !== currentConfig[field]
    );
    if (structuralChanged) {
      changes.requiresRerender = {
        fields: structuralFields.filter(
          (field) => previousConfig[field] !== currentConfig[field]
        ),
      };
    }

    return changes;
  }

  /**
   * Update quantity-related elements
   * @param {number} newQuantity - New quantity value
   * @private
   */
  #updateQuantityElements(newQuantity) {
    const quantityInput = this.querySelector(".quantity__input");
    if (quantityInput && parseInt(quantityInput.value, 10) !== newQuantity) {
      quantityInput.value = newQuantity;

      if (this.allowDebug()) {
        console.log("CartItem: Updated quantity display:", { newQuantity });
      }
    }
  }

  /**
   * Update price-related elements
   * @param {Object} config - Current configuration
   * @private
   */
  #updatePriceElements(config) {
    const priceElement = this.querySelector(".cart-item__price");
    if (priceElement) {
      // Defensive check for Safari compatibility - handle undefined price_html
      const priceContent = config.priceHtml || `<span class="price"><span class="price-item">Loading...</span></span>`;
      priceElement.innerHTML = priceContent;

      if (this.allowDebug()) {
        console.log("CartItem: Updated price display:", {
          priceHtml: config.priceHtml,
          fallbackUsed: !config.priceHtml,
        });
      }
    }
  }

  /**
   * Update discount-related elements
   * @param {Object} config - Current configuration
   * @private
   */
  #updateDiscountElements(config) {
    if (this.SKIP_SAVINGS_RENDER) return;
    if (config.disableDiscountMessaging) return;

    const savingsElement = this.querySelector(".cart-item__savings");
    const hasDiscount = config.lineItemCombinedTotalDiscount > 0;
    const savingsText = `Saving ${config.lineItemCombinedTotalDiscountMoney}`;

    if (hasDiscount) {
      if (savingsElement) {
        // Update existing savings element
        savingsElement.innerHTML = savingsText;
        savingsElement.style.display = '';
      } else {
        // Create and insert new savings element
        this.#createAndInsertSavingsElement(savingsText);
      }
    } else if (savingsElement) {
      // Hide savings element when no discount
      savingsElement.style.display = 'none';
    }

    if (this.allowDebug()) {
      console.log("CartItem: Updated discount elements:", {
        hasDiscount,
        discountAmount: config.lineItemCombinedTotalDiscountMoney,
        hasElement: !!savingsElement,
      });
    }
  }

  /**
   * Create and insert savings element in the correct DOM position
   * @param {string} savingsText - The savings text to display
   * @private
   */
  #createAndInsertSavingsElement(savingsText) {
    const newSavingsElement = document.createElement('p');
    newSavingsElement.className = 'cart-item__savings color-red';
    newSavingsElement.innerHTML = savingsText;

    const priceContainer = this.querySelector(".cart-item__price-container");
    const priceElement = this.querySelector(".cart-item__price");

    if (!priceElement || !priceContainer) return;

    // Insert after price element, before any flags
    const firstFlag = priceContainer.querySelector(".cart-item__flag");
    if (firstFlag) {
      priceContainer.insertBefore(newSavingsElement, firstFlag);
    } else {
      priceElement.insertAdjacentElement('afterend', newSavingsElement);
    }
  }

  /**
   * Update message-related elements
   * @param {Object} config - Current configuration
   * @private
   */
  #updateMessageElements(config) {
    // Get the latest HTML from render methods
    const messagesHtml = this.#renderMessages();
    const priceFlagsHtml = this.#renderPriceFlags();

    // Define elements to update with their conditions and insertion points
    const elementUpdates = [
      {
        selector: '.cart-item__preorder-message',
        condition: this.#hasPreorderMessageText(config),
        content: config.preorderMessage,
        sourceHtml: messagesHtml,
        insertionPoint: '.cart-item__price-container',
        insertionMethod: 'afterend' // Messages go after price container
      },
      {
        selector: '.cart-item__message',
        condition: config.hasMinicartMessage && config.minicartMessage,
        content: config.minicartMessage,
        sourceHtml: messagesHtml,
        insertionPoint: '.cart-item__price-container',
        insertionMethod: 'afterend' // Messages go after price container
      },
      {
        selector: '.cart-item__flag--final-sale',
        condition: config.isFinalSale,
        sourceHtml: priceFlagsHtml,
        insertionPoint: '.cart-item__price',
        insertionMethod: 'afterend' // Flags go inside price container after price element
      },
      {
        selector: '.cart-item__flag--preorder',
        condition: config.hasPreorderMessage && !this.#hasPreorderMessageText(config),
        sourceHtml: priceFlagsHtml,
        insertionPoint: '.cart-item__price',
        insertionMethod: 'afterend' // Flags go inside price container after price element
      }
    ];

    // Use DOMUtils to process all element updates
    batchUpdateElements(this, elementUpdates);

    if (this.allowDebug()) {
      console.log("CartItem: Updated message elements:", {
        hasPreorder: config.hasPreorderMessage,
        hasMinicart: config.hasMinicartMessage,
        isFinalSale: config.isFinalSale,
      });
    }
  }


  /**
   * Update availability-related elements
   * @param {Object} config - Current configuration
   * @private
   */
  #updateAvailabilityElements(config) {
    // Update quantity input constraints
    const quantityInput = this.querySelector(".quantity__input");
    if (quantityInput) {
      // Update max attribute if inventory policy is deny
      if (config.inventoryPolicy === "deny" && config.inventoryQuantity) {
        quantityInput.setAttribute("max", config.inventoryQuantity);
      } else {
        quantityInput.removeAttribute("max");
      }

      // Update readonly state if disable_quantity changed
      if (config.disableQuantity) {
        quantityInput.setAttribute("readonly", "");
      } else {
        quantityInput.removeAttribute("readonly");
      }
    }

    // Update quantity buttons if disable_quantity changed
    const decreaseButton = this.querySelector(".quantity__button--left");
    const increaseButton = this.querySelector(".quantity__button--right");

    const removeQuantityButtons = config.disableQuantity;

    if (removeQuantityButtons && (decreaseButton || increaseButton)) {
      // remove the buttons to prevent any interaction.
      if (decreaseButton) decreaseButton.remove();
      if (increaseButton) increaseButton.remove();
    } else if (!removeQuantityButtons && (!decreaseButton && !increaseButton)) {
      const quantityControls = this.querySelector(".cart-item__input");
      if (quantityControls) {
        // If buttons don't exist but should, we might need a more extensive update
        const newQuantityHtml = this.#renderQuantityControls();
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = newQuantityHtml;
        const newQuantityControlsDiv = tempDiv.querySelector(".cart-item__quantity");
        if (newQuantityControlsDiv) {
          quantityControls.closest(".cart-item__quantity").replaceWith(newQuantityControlsDiv);
        }
      }
    }

    if (this.allowDebug()) {
      console.log("CartItem: Updated availability elements:", {
        inventoryQuantity: config.inventoryQuantity,
        inventoryPolicy: config.inventoryPolicy,
        disableQuantity: config.disableQuantity,
        isGift: config.isGift,
        isGiftCard: config.isGiftCard,
        removeQuantityButtons: removeQuantityButtons,
      });
    }
  }

  /**
   * Update media-related elements
   * @param {Object} config - Current configuration
   * @private
   */
  #updateMediaElements(config) {
    const imageElement = this.querySelector(".cart-item__image img");
    const placeholderElement = this.querySelector(".cart-item__image .placeholder-image");

    if (config.featuredMedia) {
      // We have media data - update or create image element
      const imageWidth = 135
      const imageHeight = 135
      const newSrc = config.featuredMedia.replace("width=140", "width=270")

      if (imageElement) {
        // Update existing image
        if (imageElement.src !== newSrc) {
          imageElement.src = newSrc;
          imageElement.alt = config.title || '';
          imageElement.width = imageWidth;
          imageElement.height = imageHeight;
        }
      } else if (placeholderElement) {
        // Replace placeholder with actual image
        const imgElement = document.createElement('img');
        imgElement.src = newSrc;
        imgElement.alt = config.title || '';
        imgElement.width = imageWidth;
        imgElement.height = imageHeight;
        imgElement.loading = 'lazy';
        placeholderElement.replaceWith(imgElement);
      } else {
        // Re-render the entire image section
        const imageContainer = this.querySelector(".cart-item__image");
        if (imageContainer) {
          const newImageHtml = this.#renderImage();
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = newImageHtml;
          const newImageContainer = tempDiv.querySelector(".cart-item__image");
          if (newImageContainer) {
            imageContainer.replaceWith(newImageContainer);
          }
        }
      }

      if (this.allowDebug()) {
        console.log("CartItem: Updated media elements:", {
          newSrc,
          width: imageWidth,
          height: imageHeight,
          elementType: imageElement ? 'existing-img' : (placeholderElement ? 'replaced-placeholder' : 'full-rerender'),
        });
      }
    } else {
      // No media data - ensure we have a placeholder if no image exists
      if (imageElement && !placeholderElement) {
        // Replace image with placeholder for missing media
        const imageWidth = 135
        const imageHeight = 135
        const placeholder = document.createElement('div');
        placeholder.className = 'placeholder-image';
        placeholder.style.cssText = `width:${imageWidth}px;height:${imageHeight}px;background:#f5f5f5;display:flex;align-items:center;justify-content:center;color:#999;font-size:12px;`;
        placeholder.textContent = 'Loading...';
        imageElement.replaceWith(placeholder);

        if (this.allowDebug()) {
          console.log("CartItem: Replaced image with placeholder (missing media)");
        }
      }
    }
  }

  /**
   * Update title-related elements
   * @param {Object} config - Current configuration
   * @private
   */
  #updateTitleElements(config) {
    // Use the same logic as our render method for determining title
    const newTitle = config.productTitle;

    // Update main title
    const titleElement = this.querySelector(".cart-item__title");
    if (titleElement) {
      if (titleElement.textContent !== newTitle) {
        titleElement.textContent = newTitle;
      }
    }

    // Update variant title
    const variantTitleElement = this.querySelector(".cart-item__variant-title");
    if (config.variantTitle) {
      if (variantTitleElement) {
        variantTitleElement.textContent = config.variantTitle;
      } else {
        // Element doesn't exist but should - create it
        const titleElement = this.querySelector(".cart-item__title");
        if (titleElement) {
          const variantTitleEl = document.createElement("p");
          variantTitleEl.className = "cart-item__variant-title";
          variantTitleEl.textContent = config.variantTitle;
          titleElement.insertAdjacentElement("afterend", variantTitleEl);
        }
      }
    } else if (variantTitleElement) {
      variantTitleElement.remove();
    }

    if (this.allowDebug()) {
      console.log("CartItem: Updated title elements:", {
        title: config.title,
        productTitle: config.productTitle,
        variantTitle: config.variantTitle,
        displayedTitle: newTitle,
      });
    }
  }

  /**
   * Update the quantity display in the UI
   * @param {number} quantity - Optional quantity to display (defaults to currentQuantity)
   * @private
   */
  #updateQuantityDisplay(quantity = this.currentQuantity) {
    // Use the more comprehensive quantity update method
    this.#updateQuantityElements(quantity);
  }

  /**
   * Handle when this item is removed from the cart
   * @private
   */
  #handleItemRemoved() {
    if (this.allowDebug()) {
      console.log("Item removed from cart:", this.config.key);
    }

    // Add a removal animation or hide the item
    this.classList.add("cart-item--removing");

    // Remove the element after animation
    setTimeout(() => {
      this.remove();
    }, 300);
  }

  /**
   * Show error message to user
   * @param {string} message - Error message to display
   * @private
   */
  #showError(message) {
    const errorContainer = this.querySelector(".cart-item__error");
    if (errorContainer) {
      errorContainer.textContent = message;
      errorContainer.classList.remove("hidden");

      if (this.allowDebug()) {
        console.log("CartItem: Showing error message:", message);
      }

      // Auto-hide error after 5 seconds
      clearTimeout(this.errorTimeout);
      this.errorTimeout = setTimeout(() => {
        this.#hideError();
      }, 5000);
    }
  }

  /**
   * Hide error message
   * @private
   */
  #hideError() {
    const errorContainer = this.querySelector(".cart-item__error");
    if (errorContainer) {
      errorContainer.classList.add("hidden");
      clearTimeout(this.errorTimeout);

      if (this.allowDebug()) {
        console.log("CartItem: Error message hidden");
      }
    }
  }

  /**
   * Clear any existing error when successful operations occur
   * @private
   */
  #clearError() {
    this.#hideError();
  }

  /**
   * Render the cart item HTML structure
   * @private
   */
  #render() {
    if (!this.config) return;

    try {
      // Build the complete cart item HTML by combining sections
      const imageHtml = this.#renderImage();
      const bodyHtml = this.#renderBody();

      this.innerHTML = [imageHtml, bodyHtml]
        .filter(html => html && html !== 'undefined')
        .join('');
    } catch (error) {
      console.error('CartItem: Error rendering cart item:', error);
      this.innerHTML = '<div class="cart-item__error">Error rendering cart item</div>';
    }
  }

  /**
   * Render the product image section
   * @returns {string} HTML for product image
   * @private
   */
  #renderImage() {
    const itemData = this.config;
    const imageWidth = 135
    const imageHeight = 135

    let imageSrc = "";
    if (itemData.featuredMedia) {
      imageSrc = itemData.featuredMedia.replace("width=140", "width=270");
    }

    // Defensive check for Safari compatibility - don't show placeholder text initially
    const hasValidImage = itemData.featuredMedia && imageSrc;

    return `
      <div class="cart-item__image">
        <a
          id="cart-item-image-link-${itemData.handle}"
          href="${itemData.url || '#'}"
          aria-label="${decodeHTMLEntities(itemData.title || '')}"
        >
          ${hasValidImage
            ? `<img src="${imageSrc}" width="${imageWidth}" height="${imageHeight}" alt="${itemData.title || ''}" loading="lazy">`
            : `<div class="placeholder-image" style="width:${imageWidth}px;height:${imageHeight}px;background:#f5f5f5;display:flex;align-items:center;justify-content:center;color:#999;font-size:12px;">Loading...</div>`
          }
        </a>
      </div>
    `;
  }

  /**
   * Render the cart item body section
   * @returns {string} HTML for cart item body
   * @private
   */
  #renderBody() {
    return `
      <div class="cart-item__body">
        ${this.#renderTitles()}
        <div class="cart-item__price-container">
          ${this.#renderPrice()}
          ${this.#renderSavings()}
          ${this.#renderPriceFlags()}
        </div>
        ${this.#renderQuantityControls()}
        ${this.#renderMessages()}
        ${this.#renderErrorContainer()}
      </div>
    `;
  }

  /**
   * Render product and variant titles
   * @returns {string} HTML for titles
   * @private
   */
  #renderTitles() {
    const itemData = this.config;
    const mainTitle = itemData.productTitle;

    let html = `<p class="cart-item__title">${mainTitle}</p>`;

    if (itemData.variantTitle) {
      html += `<p class="cart-item__variant-title">${itemData.variantTitle}</p>`;
    }

    if (itemData.itemComponents && itemData.itemComponents.length > 0) {
      html += `<ul class="cart-item__item-components">
        ${itemData.itemComponents.map(component => `<li class="cart-item__item-component">${component.title}</li>`).join('')}
      </ul>`;
    }

    const visibleProperties = this.#getVisibleLineItemProperties(itemData.properties);
    if (visibleProperties.length > 0) {
      html += `<ul class="cart-item__properties">
        ${visibleProperties.map(({ key, value }) => `<li class="cart-item__property">${key}: ${value}</li>`).join('')}
      </ul>`;
    }

    return html;
  }

  /**
   * Render price information
   * @returns {string} HTML for price
   * @private
   */
  #renderPrice() {
    const itemData = this.config;
    // Defensive check for Safari compatibility - handle undefined price_html
    const priceContent = itemData.priceHtml || `<span class="price"><span class="price-item">Loading...</span></span>`;
    return `<p class="cart-item__price">${priceContent}</p>`;
  }

  /**
   * Render savings information
   * @returns {string} HTML for savings
   * @private
   */
  #renderSavings() {
    if (this.SKIP_SAVINGS_RENDER) return "";
    const itemData = this.config;
    if (!(itemData.lineItemCombinedTotalDiscount > 0)) return "";
    if (itemData.disableDiscountMessaging) return "";
    return `<p class="cart-item__savings color-red">Saving ${itemData.lineItemCombinedTotalDiscountMoney}</p>`;
  }

  /**
   * Render price element flags such as final sale or preorder
   * @returns {string} HTML for price element flags
   * @private
   */
  #renderPriceFlags() {
    const itemData = this.config;
    let html = "";
    if (itemData.isFinalSale) {
      html += `<p class="cart-item__flag cart-item__flag--final-sale color-red">FINAL SALE</p>`;
    }
    if (itemData.hasPreorderMessage && !this.#hasPreorderMessageText(itemData)) {
      html += `<p class="cart-item__flag cart-item__flag--preorder color-red">PREORDER</p>`;
    }
    const allocations = itemData.discountAllocations ?? [];
    for (const allocation of allocations) {
      if (!(allocation.amount > 0)) continue;
      html += `<p class="cart-item__flag cart-item__flag--discount-title color-red">${allocation.title}</p>`;
    }

    const promotionText = String(itemData.promotionMessage || "").trim();
    const discountMessageText = String(
      this.#getLineItemPropertyValue(itemData.properties, "_discountMessage_Functions") || ""
    ).trim();
    const discountType = String(
      this.#getLineItemPropertyValue(itemData.properties, "_discountType_Functions") || ""
    ).trim();
    const discountAmountOff = String(
      this.#getLineItemPropertyValue(itemData.properties, "_discountAmountOff_Functions") || ""
    ).trim();
    const discountPercentOff = String(
      this.#getLineItemPropertyValue(itemData.properties, "_discountPercentOff_Functions") || ""
    ).trim();

    const hasFunctionsDiscountMetadata = Boolean(
      discountMessageText || discountType || discountAmountOff || discountPercentOff
    );

    const expectedDiscountTitle = (discountMessageText || promotionText).toLowerCase();
    const promotionMatchesDiscount = allocations.some((allocation) => {
      if (!(allocation.amount > 0)) return false;
      return String(allocation.title || "").trim().toLowerCase() === expectedDiscountTitle;
    });

    const shouldRenderPromotionMessage =
      itemData.hasPromotionMessage &&
      promotionText &&
      (!hasFunctionsDiscountMetadata || promotionMatchesDiscount);

    if (shouldRenderPromotionMessage) {
      html += `<p class="cart-item__flag cart-item__flag--promotion-message color-red">${itemData.promotionMessage}</p>`;
    }
    return html;
  }

  #getLineItemPropertyValue(properties, key) {
    if (!properties || !key) return "";

    if (Array.isArray(properties)) {
      for (const property of properties) {
        if (!Array.isArray(property) || property.length < 2) continue;
        if (property[0] === key) return property[1];
      }
      return "";
    }

    if (typeof properties === "object") {
      return properties[key];
    }

    return "";
  }

  /**
   * Get customer-visible line item properties (keys not prefixed with "_")
   * @param {Array|Object|null} properties - Line item properties
   * @returns {Array<{key: string, value: string}>} Visible property key/value pairs
   * @private
   */
  #getVisibleLineItemProperties(properties) {
    if (!properties) return [];

    const visibleProperties = [];

    if (Array.isArray(properties)) {
      for (const property of properties) {
        if (!Array.isArray(property) || property.length < 2) continue;

        const key = property[0];
        const value = property[1];

        if (typeof key !== "string" || key.startsWith("_")) continue;
        if (value == null || String(value).trim() === "") continue;

        visibleProperties.push({ key, value: String(value) });
      }

      return visibleProperties;
    }

    if (typeof properties === "object") {
      for (const [key, value] of Object.entries(properties)) {
        if (key.startsWith("_")) continue;
        if (value == null || String(value).trim() === "") continue;

        visibleProperties.push({ key, value: String(value) });
      }
    }

    return visibleProperties;
  }

  /**
   * Whether the item has displayable preorder message text
   * @param {Object} itemData - Item configuration
   * @returns {boolean}
   * @private
   */
  #hasPreorderMessageText(itemData) {
    return itemData.hasPreorderMessage && String(itemData.preorderMessage).trim() !== "";
  }

  /**
   * Render message elements (preorder, minicart, etc.)
   * @returns {string} HTML for messages
   * @private
   */
  #renderMessages() {
    const itemData = this.config;
    let html = "";

    if (this.#hasPreorderMessageText(itemData)) {
      html += `<p class="cart-item__preorder-message color-red">${itemData.preorderMessage}</p>`;
    }

    if (itemData.hasMinicartMessage && String(itemData.minicartMessage).trim() !== "") {
      html += `<p class="cart-item__message color-red">${itemData.minicartMessage}</p>`;
    }

    return html;
  }

  /**
   * Render quantity controls and remove button
   * @returns {string} HTML for quantity controls
   * @private
   */
  #renderQuantityControls() {
    const itemData = this.config;
    const variantId = itemData.variantId || 'unknown';

    return `
      <div class="cart-item__quantity">
        <label for="quantity-${variantId}">Qty</label>
        <div class="cart-item__input">
          ${this.#renderQuantityDecreaseButton()}
          ${this.#renderQuantityInput()}
          ${this.#renderQuantityIncreaseButton()}
        </div>
        ${this.#renderRemoveButton()}
      </div>
    `;
  }

  /**
   * Render quantity decrease/increase buttons
   * @returns {string} HTML for quantity buttons
   * @private
   */
  #renderQuantityDecreaseButton() {
    if (this.config.disableQuantity) return "";

    return `
      <button type="button" class="quantity__button--left" data-action="decrease" aria-label="Decrease quantity">&ndash;</button>
    `;
  }

  #renderQuantityIncreaseButton() {
    if (this.config.disableQuantity) return "";

    return `
      <button type="button" class="quantity__button--right" data-action="increase" aria-label="Increase quantity">+</button>
    `;
  }

  /**
   * Render quantity input field
   * @returns {string} HTML for quantity input
   * @private
   */
  #renderQuantityInput() {
    const itemData = this.config;

    // Set max attribute if inventory policy is deny and we have inventory data
    const maxAttribute = (itemData.inventoryPolicy === "deny" && itemData.inventoryQuantity)
      ? `max="${itemData.inventoryQuantity}"`
      : "";

    const variantId = itemData.variantId || 0;
    const quantity = itemData.quantity || this.currentQuantity || 0;

    return `
      <input
        type="number"
        class="quantity__input cart-item__quantity-display"
        id="quantity-${variantId}"
        name="quantity"
        value="${quantity}"
        min="0"
        ${maxAttribute}
        step="1"
        data-action="quantity-input"
        aria-label="Quantity"
        ${itemData.disableQuantity ? "readonly" : ""}
      >
    `;
  }

  /**
   * Render remove button
   * @returns {string} HTML for remove button
   * @private
   */
  #renderRemoveButton() {
    // Hide remove button for membership bundle items that are NOT the main item
    if (this.config.disableRemove) return "";

    return `
      <div class="cart-item__remove">
        <button type="button" class="button-remove" data-action="remove" aria-label="Remove item">
          Remove
        </button>
      </div>
    `;
  }

  /**
   * Render error container
   * @returns {string} HTML for error container
   * @private
   */
  #renderErrorContainer() {
    return `<div class="cart-item__error hidden"></div>`;
  }

  /**
   * Override the base class component-specific initialization
   * @protected
   */
  async _initializeComponent() {
    this.currentQuantity = parseInt(this.config?.quantity, 10) || 0;
    this.lastKnownGoodQuantity = this.currentQuantity; // Initialize last known good quantity

    if (this.allowDebug()) {
      console.log("CartItem initialized:", {
        config: this.config,
        currentQuantity: this.currentQuantity,
        lastKnownGoodQuantity: this.lastKnownGoodQuantity
      });
    }

    // Render the HTML structure
    this.#render();
  }

  /**
   * Override the base class event listener setup
   * @protected
   */
  _setupEventListeners() {
    // Use event delegation for better performance
    this.addEventListener("click", this.handleClick);
    this.addEventListener("input", this.handleInput);

    this._subscribe(EVENTS.CART_STATE_CHANGED, this.handleStateChange);
    this._subscribe(EVENTS.CART_ITEMS_STATE_UPDATED, this.#handleStateUpdate.bind(this));
    this._subscribe(EVENTS.CART_ITEM_QUANTITY_CHANGE_CONFIRMED, this.#handleQuantityChangeConfirmed.bind(this));
    this._subscribe(EVENTS.CART_ITEM_QUANTITY_CHANGE_REQUESTED, this.#handleQuantityChangeRequest.bind(this));

    // Note: We no longer cache state manager references, so no need to listen for manager updates
  }

  /**
   * Handle state updates from CartItems
   * @param {Object} data - State updated data
   * @private
   */
  #handleStateUpdate(data) {
    const { state } = data;

    // Since we only have one cart drawer, no need to check containerId

    // Guard against null or invalid state
    if (!state || !state.items) {
      if (this.allowDebug()) {
        console.log(
          "CartItem: No valid state received, skipping update"
        );
      }
      return;
    }

    // Find item data for this specific cart item
    const itemData = state.items.find(
      (item) => item.key === this.config.key
    );

    if (!itemData) {
      if (this.allowDebug()) {
        console.log(
          "CartItem: No item data found for item:",
          this.config.key
        );
      }
      return;
    }

    if (this.allowDebug()) {
      console.log("CartItem: Applying state update:", {
        key: itemData.key,
        oldPriceHtml: this.config.priceHtml,
        newPriceHtml: itemData.priceHtml,
      });
    }

    // Update this item with fresh data from state
    this.updateFromState(itemData);
  }

  /**
   * Handle quantity change confirmations
   * @param {Object} data - The quantity change confirmation data
   * @private
   */
  #handleQuantityChangeConfirmed(data) {
    const { key, success } = data;

    // Only handle confirmations for this cart item
    if (key !== this.config.key) {
      return;
    }

    if (this.allowDebug()) {
      console.log("CartItem: Quantity change confirmed", {
        key,
        success,
        data
      });
    }

    // The actual UI updates will come through greyson:cart:changed events
    // This is just for any additional UI feedback if needed
  }

  /**
   * Handle quantity change requests with debouncing
   * @param {Object} data - The quantity change request data
   * @private
   */
  #handleQuantityChangeRequest(data) {
    const { key, newQuantity, debounceMs, requestId } = data;

    // Only handle requests for this cart item
    if (key !== this.config.key) {
      return;
    }

    if (this.allowDebug()) {
      console.log("CartItem: Handling quantity change request", {
        key,
        newQuantity,
        debounceMs,
        requestId,
        currentQuantity: this.currentQuantity
      });
    }

    // Clear any existing timeout
    if (this.quantityChangeTimeout) {
      clearTimeout(this.quantityChangeTimeout);
      this.quantityChangeTimeout = null;
    }

    // Set up new debounced execution
    this.quantityChangeTimeout = setTimeout(async () => {
      try {
        // Execute the quantity change
        const result = await this.#executeQuantityChange(newQuantity);

        publish(EVENTS.CART_ITEM_QUANTITY_CHANGE_CONFIRMED, {
          key,
          newQuantity,
          requestId,
          success: true,
          result
        });

      } catch (error) {
        // Error is handled by #executeQuantityChange method
        this._handleError(error, "handling quantity change request");
      } finally {
        this.isQuantityChangePending = false;
        this.classList.remove("cart-item--updating");
        this.quantityChangeTimeout = null;
      }
    }, debounceMs || this.debounceDelay);
  }

  /**
   * Request a quantity change via events
   * @param {number} quantityDifference - Change in quantity (positive or negative)
   * @param {number} multiplier - Multiplier for the change
   * @private
   */
  #requestQuantityChange(quantityDifference, multiplier = 1) {
    const actualChange = quantityDifference * multiplier;
    const newQuantity = Math.max(0, this.currentQuantity + actualChange);

    if (this.allowDebug()) {
      console.log("CartItem: Requesting quantity change via event", {
        key: this.config.key,
        currentQuantity: this.currentQuantity,
        quantityDifference,
        multiplier,
        actualChange,
        newQuantity
      });
    }

    // Show loading state immediately
    this.isQuantityChangePending = true;
    this.classList.add("cart-item--updating");

    // Update current quantity optimistically for immediate UI feedback
    this.currentQuantity = newQuantity;
    this.#updateQuantityDisplay(newQuantity);

    // Dispatch quantity change request
    const requestId = `qty-change-${this.config.key}-${Date.now()}`;
    publish(EVENTS.CART_ITEM_QUANTITY_CHANGE_REQUESTED, {
      key: this.config.key,
      newQuantity,
      debounceMs: this.debounceDelay,
      requestId
    });
  }

  /**
   * Override base class cleanup to include component-specific cleanup
   * @protected
   */
  _cleanup() {
    // Clean up component event listeners
    this.removeEventListener("click", this.handleClick);
    this.removeEventListener("input", this.handleInput);

    // Call base class cleanup (handles tracked listeners and timeouts)
    super._cleanup();

    if (this.allowDebug()) {
      console.log(
        "CartItem: Cleaned up component-specific listeners and timeouts"
      );
    }
  }
}

if (!customElements.get("cart-item")) {
  customElements.define("cart-item", CartItem);
}

export default CartItem;
